/**
 * NOTE ON PROVENANCE, for anyone checking these tables against the BRD.
 *
 * Spec §6 tabulates seven columns: Create/Edit, Submit, Approve/Reject, View Own,
 * View All, Configure, Delegate. Those grants below are transcribed directly and
 * should match the spec cell for cell.
 *
 * Three primitives have no column in that table — manage_integrations,
 * manage_ai_settings and export. Their grants are DERIVED from spec §13, which
 * assigns the Integration Cockpit, AI Settings and Excel export to specific
 * administrators. Each derived grant is marked [§13] inline. If Algihaz correct
 * one, it is a data change in this file, not a code change anywhere else.
 */
// ---------------------------------------------------------------------------
// Platform Roles — organisation-wide, exactly one per user (ADR-0005)
// ---------------------------------------------------------------------------
export const PLATFORM_ROLE_KEYS = [
    'super_admin',
    'org_admin',
    'executive_viewer',
    'bu_project_head',
    'quality_manager',
    'employee',
    'guest_auditor',
];
export const PLATFORM_ROLES = {
    super_admin: {
        key: 'super_admin',
        label: 'Super Admin',
        description: 'Full authority across all three applications. Cannot edit or delete audit log entries — nothing can (ADR-0006).',
        permissions: {
            create_edit: { scope: 'all' },
            submit: { scope: 'all' },
            approve_reject: { scope: 'all' },
            view_own: { scope: 'all' },
            view_all: { scope: 'all' },
            configure_masters: { scope: 'all' },
            manage_integrations: { scope: 'all' }, // [§13]
            manage_ai_settings: { scope: 'all' }, // [§13]
            export: { scope: 'all' }, // [§13]
            delegate: { scope: 'all' },
        },
    },
    org_admin: {
        key: 'org_admin',
        label: 'Org Admin',
        description: 'Administers configuration, master data, roles and integrations. Deliberately holds no transactional submit or approve authority.',
        permissions: {
            create_edit: { scope: 'all', only: 'master_data' },
            view_all: { scope: 'all' },
            configure_masters: { scope: 'all' },
            manage_integrations: { scope: 'all' }, // [§13]
            manage_ai_settings: { scope: 'all' }, // [§13]
            export: { scope: 'all' }, // [§13]
            delegate: { scope: 'all', only: 'admin_roles' },
        },
    },
    executive_viewer: {
        key: 'executive_viewer',
        label: 'Executive Viewer',
        description: 'Reads everything; approves only within an assigned scope.',
        permissions: {
            approve_reject: { scope: 'scoped' },
            view_own: { scope: 'all' },
            view_all: { scope: 'all' },
            export: { scope: 'all' }, // [§13]
            delegate: { scope: 'scoped' },
        },
    },
    bu_project_head: {
        key: 'bu_project_head',
        label: 'BU / Project Head',
        description: 'Approves and views within their own Business Unit or Project.',
        permissions: {
            approve_reject: { scope: 'own' },
            view_own: { scope: 'all' },
            view_all: { scope: 'own' },
            export: { scope: 'own' }, // [§13]
            delegate: { scope: 'own' },
        },
    },
    quality_manager: {
        key: 'quality_manager',
        label: 'Quality Manager',
        description: 'Reads everything; approves within an assigned scope.',
        permissions: {
            approve_reject: { scope: 'scoped' },
            view_own: { scope: 'all' },
            view_all: { scope: 'all' },
            export: { scope: 'all' }, // [§13]
            delegate: { scope: 'scoped' },
        },
    },
    employee: {
        key: 'employee',
        label: 'Employee (base)',
        description: 'Grants nothing on its own. An Employee can sign in and reach an app home screen, but sees no functional modules until a Workspace Role is also assigned — a specified behaviour, not a bug (ADR-0005).',
        permissions: {},
    },
    guest_auditor: {
        key: 'guest_auditor',
        label: 'External / Guest Auditor',
        description: 'Time-boxed, read-only, scoped to one audit engagement. Auto-expires at engagement close; expiry is enforced server-side on every request.',
        permissions: {
            view_own: { scope: 'scoped', only: 'engagement' },
        },
    },
};
// ---------------------------------------------------------------------------
// Workspace Roles — scoped to one app, zero or more per user (ADR-0005)
// ---------------------------------------------------------------------------
//
// A Workspace Role always carries a scope of one or more BUs/Projects. Within a
// Workspace Role, a grant of 'all' therefore means "all records inside this
// role's assigned scope", never organisation-wide. Organisation-wide reach comes
// only from the Platform Role layer.
export const WORKSPACE_ROLE_KEYS = [
    'qaqc_representative',
    'document_controller',
    'approver_reviewer',
    'form_creator',
    'form_approver',
    'audit_program_manager',
    'audit_team_lead',
    'process_product_owner',
];
export const WORKSPACE_ROLES = {
    qaqc_representative: {
        key: 'qaqc_representative',
        app: 'qaqc',
        label: 'QAQC Representative',
        description: 'Enters and submits the monthly QAQC report for their projects.',
        permissions: {
            create_edit: { scope: 'all' },
            submit: { scope: 'all' },
            view_own: { scope: 'all' },
            export: { scope: 'all' }, // [§13]
            delegate: { scope: 'all' },
        },
    },
    document_controller: {
        key: 'document_controller',
        app: 'qaqc',
        label: 'Document Controller',
        description: 'Maintains the Daily Document Governance Log.',
        permissions: {
            create_edit: { scope: 'all' },
            submit: { scope: 'all' },
            view_own: { scope: 'all' },
            export: { scope: 'all' }, // [§13]
            delegate: { scope: 'all' },
        },
    },
    approver_reviewer: {
        key: 'approver_reviewer',
        app: 'qaqc',
        label: 'Approver / Reviewer',
        description: 'Approves or sends back submitted QA/QC reports and logs.',
        permissions: {
            approve_reject: { scope: 'all' },
            view_own: { scope: 'all' },
            export: { scope: 'all' }, // [§13]
            delegate: { scope: 'all' },
        },
    },
    form_creator: {
        key: 'form_creator',
        app: 'lesson-learned',
        label: 'Form Creator',
        description: 'Raises and submits Lesson Learned forms, including offline in the field.',
        permissions: {
            create_edit: { scope: 'all' },
            submit: { scope: 'all' },
            view_own: { scope: 'all' },
            export: { scope: 'all' }, // [§13]
            delegate: { scope: 'all' },
        },
    },
    form_approver: {
        key: 'form_approver',
        app: 'lesson-learned',
        label: 'Form Approver',
        description: 'Approves a Lesson Learned form, or sends it back with mandatory remarks, until approved.',
        permissions: {
            approve_reject: { scope: 'all' },
            view_own: { scope: 'all' },
            export: { scope: 'all' }, // [§13]
            delegate: { scope: 'all' },
        },
    },
    audit_program_manager: {
        key: 'audit_program_manager',
        app: 'audit',
        label: 'Audit Program Manager',
        description: 'Builds the annual audit schedule and administers App 3 master data. The only Workspace Role with View All.',
        permissions: {
            create_edit: { scope: 'all' },
            submit: { scope: 'all' },
            view_own: { scope: 'all' },
            view_all: { scope: 'all' },
            configure_masters: { scope: 'all', only: 'master_data' },
            export: { scope: 'all' }, // [§13]
            delegate: { scope: 'all' },
        },
    },
    audit_team_lead: {
        key: 'audit_team_lead',
        app: 'audit',
        label: 'Audit Team Lead / Auditor',
        description: 'Runs the audit, classifies findings, and verifies CAR effectiveness. Approval authority is limited to CARs.',
        permissions: {
            create_edit: { scope: 'all' },
            submit: { scope: 'all' },
            approve_reject: { scope: 'all', only: 'car' },
            view_own: { scope: 'all' },
            export: { scope: 'all' }, // [§13]
            delegate: { scope: 'all' },
        },
    },
    process_product_owner: {
        key: 'process_product_owner',
        app: 'audit',
        label: 'Process / Product Owner',
        description: 'Owns the CARs raised against their department — root cause, correction, corrective action. Can edit CARs only.',
        permissions: {
            create_edit: { scope: 'all', only: 'car' },
            submit: { scope: 'all' },
            view_own: { scope: 'all' },
            export: { scope: 'own' }, // [§13]
            delegate: { scope: 'all' },
        },
    },
};
export function workspaceRolesForApp(app) {
    return Object.values(WORKSPACE_ROLES).filter((role) => role.app === app);
}
//# sourceMappingURL=roles.js.map